import { EpaGridOrderByFetchParams } from "../../../leap-components/Grid/EpaGridProps";
import { API_URL } from "./Config";
import { FetchResponse } from "../../FetchResponse";
import { PageResult } from "../../Pagination";

export interface ICertificateOfRegistration {
  licence_id: string;
  reg_no: string;
  licence_type: string;
  licence_name: string;
  valid_application_date: string;
  site_name: string;
  site_computed: string;
  local_authority_org_name: string;
  date_of_licence: string;
  licence_status: string;
  licence_application_status: string;
  main_class_of_activity: string;
  waste_category: string;
  expiry_date: string;
}

export interface ICertificateOfRegistrationDetails {
  licence_id: string;
  reg_no: string;
  licence_type: string;
  licence_name: string;
  valid_application_date: string;
  site_name: string;
  site_computed: string;
  local_authority_org_name: string;
  date_of_licence: string;
  licence_status: string;
  licence_application_status: string;
  main_class_of_activity: string;
  waste_category: string;
  expiry_date: string;
}

export interface ICertificateOfRegistrationDetailsResponse {
  response: FetchResponse;
  certificateOfRegistration?: ICertificateOfRegistrationDetails;
}

export async function GetCertificateOfRegistrationByIdFetchUrl(
  licence_id: string
): Promise<ICertificateOfRegistrationDetailsResponse> {
  var url = `${API_URL}/Licence/corbyid?licence_id=${licence_id}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-details`;
  }

  var response = await fetch(url);

  if (!response.ok) {
    return {
      response: {
        ok: false,
        httpStatusCode: response.status,
        httpErr: response.statusText,
      },
    };
  }

  var body = <ICertificateOfRegistrationDetails>await response.json();

  const certificateOfRegistration = <ICertificateOfRegistrationDetails>{
    licence_id: body.licence_id,
    reg_no: body.reg_no,
    licence_type: body.licence_type,
    licence_name: body.licence_name,
    valid_application_date: body.valid_application_date,
    site_name: body.site_name,
    site_computed: body.site_computed,
    local_authority_org_name: body.local_authority_org_name,
    date_of_licence: body.date_of_licence,
    licence_status: body.licence_status,
    licence_application_status: body.licence_application_status,
    main_class_of_activity: body.main_class_of_activity,
    waste_category: body.waste_category,
    expiry_date: body.expiry_date,
  };

  return <ICertificateOfRegistrationDetailsResponse>{
    response: {},
    certificateOfRegistration: certificateOfRegistration,
  };
}

const convertColumnNameToFilterParam = (filterName: string) => {
  switch (filterName) {
    case "licence_status":
      return "status";
    case "local_authority_org_name":
      return "local_authority";
    case "waste_category":
      return "waste_category";
    case "main_class_of_activity":
      return "class_of_activity";
    default:
      return filterName;
  }
};

export const GetCertificatesOfRegistrationFetchUrl = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;

      if (f.value != "All")
        params += `&${convertColumnNameToFilterParam(filterId)}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Date Received":
        orderByParam = "date";
        break;

      case "Reg Number":
      default:
        orderByParam = "regno";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}${
      orderBy.orderByAscending ? "" : "[desc]"
    }`;
  }

  let url = `${API_URL}/Licence/corlicenceslist${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/certificates-of-registration`;
  }

  return url;
};

export const GetCertificatesOfRegistrationCsv = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;

      if (f.value != "All")
        params += `&${convertColumnNameToFilterParam(filterId)}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Date Received":
        orderByParam = "date";
        break;

      case "Reg Number":
      default:
        orderByParam = "regno";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}${
      orderBy.orderByAscending ? "" : "[desc]"
    }`;
  }

  let url = `${API_URL}/Licence/corlicenceslistcsv${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/certificates-of-registration`;
  }

  return url;
};

export const GetFilteredDocumentsFetchUrl = (
  licenceId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  let params = `?licence_id=${licenceId}&page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;
      params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/Document/listbylicenceid${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-documents`;
  }

  return url;
};

export interface ICertificateOfRegistrationStatusFilterValue {
  status: string;
}

export interface ICertificateOfRegistrationStatusesList extends PageResult {
  list: ICertificateOfRegistrationStatusFilterValue[];
}

export async function GetStatusFilterValues(): Promise<ICertificateOfRegistrationStatusesList> {
  let url = `${API_URL}/Licence/corlicencestatusfilter`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-licences-status-filter`;
  }

  let response = await fetch(url);
  let body = <ICertificateOfRegistrationStatusesList>await response.json();
  body.list = body.list.map((item) => {
    return <ICertificateOfRegistrationStatusFilterValue>{
      status: item.status,
    };
  });
  return body;
}

export interface ICertificateOfRegistrationLocalAuthorityValue {
  local_authority: string;
}

export interface ICertificateOfRegistrationLocalAuthoritiesList
  extends PageResult {
  list: ICertificateOfRegistrationLocalAuthorityValue[];
}

export async function GetLocalAuthorityFilterValues(): Promise<ICertificateOfRegistrationLocalAuthoritiesList> {
  let url = `${API_URL}/Licence/corlicencelocalauthoritiesfilter`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-licences-local-authorities-filter`;
  }

  let response = await fetch(url);
  let body = <ICertificateOfRegistrationLocalAuthoritiesList>(
    await response.json()
  );
  body.list = body.list.map((item) => {
    return <ICertificateOfRegistrationLocalAuthorityValue>{
      local_authority: item.local_authority,
    };
  });
  return body;
}

export interface ICertificateOfRegistrationWasteCategoryValue {
  waste_category: string;
}

export interface ICertificateOfRegistrationWasteCategoriesList
  extends PageResult {
  list: ICertificateOfRegistrationWasteCategoryValue[];
}

export async function GetWasteCategoryFilterValues(): Promise<ICertificateOfRegistrationWasteCategoriesList> {
  let url = `${API_URL}/Licence/corlicencewastecategoriesfilter`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-licences-waste-categories-filter`;
  }

  let response = await fetch(url);
  let body = <ICertificateOfRegistrationWasteCategoriesList>(
    await response.json()
  );
  body.list = body.list.map((item) => {
    return <ICertificateOfRegistrationWasteCategoryValue>{
      waste_category: item.waste_category,
    };
  });
  return body;
}

export interface ICertificateOfRegistrationClassOfActivityValue {
  class_of_activity: string;
}

export interface ICertificateOfRegistrationClassesOfActivityList
  extends PageResult {
  list: ICertificateOfRegistrationClassOfActivityValue[];
}

export async function GetClassOfActivityFilterValues(): Promise<ICertificateOfRegistrationClassesOfActivityList> {
  let url = `${API_URL}/Licence/corlicenceclassessofactivitiesfilter`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/cor-licences-classes-of-activity-filter`;
  }

  let response = await fetch(url);
  let body = <ICertificateOfRegistrationClassesOfActivityList>(
    await response.json()
  );
  body.list = body.list.map((item) => {
    return <ICertificateOfRegistrationClassOfActivityValue>{
      class_of_activity: item.class_of_activity,
    };
  });
  return body;
}
